import Box from '@mui/material/Box';
import { Fragment, useContext, useState } from 'react';
import Maybe from 'true-myth/maybe';

import { TableBar } from './TableBar';
import { TableBarSearch } from './TableBarSearch';
import { usePersistTableSort } from './use-persist-table-sort';
import { defaultPagination } from './use-talent-directory-params';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import { ContentWrapper } from '../../../design/components/containers/ContentWrapper';
import { useTablePagination } from '../../common/data-grids/common/use-table-pagination';
import { EmployeesDataGrid } from '../../common/data-grids/employees/EmployeesDataGrid';
import { useEmployeesColumns } from '../../common/data-grids/employees/use-employees-columns';
import { useNavigationToEntity } from '../../common/use-navigation-to-entity';
import { ComponentErrorBoundary } from '../../error-handling/ErrorBoundaries';
import { useSnackbars } from '../../error-handling/SnackBars';
import { useTalentDirectoryEmployeeExport } from '../../query-hooks/talent-directory/talent-directory';
import { useGlobalSearchDirectoryUsers } from '../../query-hooks/talent-directory/use-global-search-directory-users';
import { useTalentDirectoryEmployeeFilter } from '../../query-hooks/use-talent-directory-employee-filter';

import type { EmployeeRow } from '../../common/data-grids/employees/EmployeesDataGrid';
import type { FunctionComponent } from 'react';

export const EmployeesTable: FunctionComponent = () => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { paginationModel, setPaginationModel } = useTablePagination();
    const { navigateToUserProfile } = useNavigationToEntity();
    const searchTerm = Maybe.of(applicationState.locationSearchParams.get('search'));
    const [sortOptions, saveSortOptions] = usePersistTableSort('talentDirectoryEmployeeSort');
    const [isSearchFieldOpen, setIsSearchFieldOpen] = useState<boolean>(searchTerm.isJust);
    const filterQuery = useTalentDirectoryEmployeeFilter({
        enabled: true,
    });
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const { isPending: isExporting, mutate: exportEmployees } = useTalentDirectoryEmployeeExport();
    const { data, isFetching, isPending } = useGlobalSearchDirectoryUsers({
        filterPayload: filterQuery.data?.filterPayload ?? Maybe.nothing(),
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        searchTerm,
        sortOptions,
    });
    const onExportButtonClick = (): void => {
        exportEmployees(undefined, {
            onError: showErrorSnackbar,
            onSuccess: ({ downloadLink }) => {
                showSuccessSnackbar('success.users.exported');
                window.open(downloadLink, '_blank');
            },
        });
    };
    const columns = useEmployeesColumns();
    const users = data?.data ?? [];
    const pagination = data?.pagination ?? defaultPagination;
    const rows: readonly EmployeeRow[] = users.map((user) => {
        return {
            id: user.id,
            emailAddress: user.emailAddress ?? undefined,
            firstName: user.firstName,
            lastName: user.lastName,
            skills: user.skills,
        };
    });
    const noDataMessage = translate('pages.global-search.no-data.employees');

    return (
        <Fragment>
            {filterQuery.isSuccess && (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        justifyContent: 'flex-end',
                        minHeight: '56px',
                        px: 2,
                    }}
                >
                    {isSearchFieldOpen ? (
                        <TableBarSearch onSearchBarCancelButtonClick={() => setIsSearchFieldOpen(false)} />
                    ) : (
                        <TableBar
                            filters={filterQuery.data.filters}
                            hasActiveFilters={filterQuery.data.hasActiveFilters}
                            isExporting={isExporting}
                            resetFilterValues={filterQuery.data.resetFilterValues}
                            saveFilterValues={filterQuery.data.saveFilterValues}
                            onExportButtonClick={onExportButtonClick}
                            onSearchButtonClick={() => setIsSearchFieldOpen(true)}
                        />
                    )}
                </Box>
            )}
            <ContentWrapper>
                <ComponentErrorBoundary message="errors.component.global-search.employees">
                    <EmployeesDataGrid
                        columns={columns}
                        isFetching={isFetching}
                        isPending={isPending}
                        noDataMessage={noDataMessage}
                        paginationModel={paginationModel}
                        rowCount={pagination.count}
                        rows={rows}
                        sortOptions={sortOptions}
                        onPaginationModelChange={setPaginationModel}
                        onRowClick={navigateToUserProfile}
                        onSortOptionsChange={saveSortOptions}
                    />
                </ComponentErrorBoundary>
            </ContentWrapper>
        </Fragment>
    );
};
